/**
 * Íconos de la app: SVG monocromos que heredan `currentColor`.
 *
 * Un solo vocabulario para los tres lugares donde hay íconos —las pestañas del
 * caso, la barra de arriba cuando se encoge y la barra de abajo del celular—
 * para no aprender tres. Antes eran emoji: pintan en su propio color (así que
 * el estado activo solo se notaba por el fondo), se ven distinto en cada
 * sistema operativo y a 20 px varios se confunden entre sí — 📚 🔗 🛡️ ⚙️ eran
 * cuatro manchitas de colores.
 *
 * Dibujados a mano: doce íconos no justifican meter una librería de ~50 KB al
 * bundle, que ya viene pesado. Trazo de 1.8 para que se lean a 20 px.
 */

type Props = { className?: string };

function Svg({ className, children }: Props & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ?? "h-5 w-5"}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

// --- pestañas del caso ------------------------------------------------------

/** Bitácora: hoja con renglones escritos. */
export function IconoNotas(p: Props) {
  return (
    <Svg {...p}>
      <path d="M5 3.5h9.5L19 8v12.5H5z" />
      <path d="M14 3.5V8h5" />
      <path d="M8.5 12.5h7M8.5 16h4.5" />
    </Svg>
  );
}

/** Equipos: un chip — es hardware, se lee como "el aparato". */
export function IconoImeis(p: Props) {
  return (
    <Svg {...p}>
      <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
      <rect x="10" y="10" width="4" height="4" rx="0.5" />
      <path d="M9.5 3.5v3M14.5 3.5v3M9.5 17.5v3M14.5 17.5v3M3.5 9.5h3M3.5 14.5h3M17.5 9.5h3M17.5 14.5h3" />
    </Svg>
  );
}

/** Evidencias: una foto. Casi todo lo que se sube son fotos y videos del equipo. */
export function IconoEvidencias(p: Props) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path d="M4 16.5l4.5-4 3.5 3 3-2.5 5 4.5" />
    </Svg>
  );
}

/** Correos: sobre. */
export function IconoCorreos(p: Props) {
  return (
    <Svg {...p}>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M3 6.5l9 6.5 9-6.5" />
    </Svg>
  );
}

/** Recordatorios: despertador — avisa a una hora, no es una alerta cualquiera. */
export function IconoRecordatorios(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="13" r="7.5" />
      <path d="M12 9.5V13l2.5 1.5" />
      <path d="M5 4.5L2.8 6.7M19 4.5l2.2 2.2" />
    </Svg>
  );
}

// --- navegación -------------------------------------------------------------

/** Panel: casa. */
export function IconoPanel(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3.5 10.5L12 3.5l8.5 7" />
      <path d="M5.5 9.5v10h13v-10" />
      <path d="M9.5 19.5v-6h5v6" />
    </Svg>
  );
}

/** Estadísticas: barras. */
export function IconoEstadisticas(p: Props) {
  return (
    <Svg {...p}>
      <path d="M4 20.5h16" />
      <path d="M6.5 20.5V12M11 20.5V5.5M15.5 20.5v-6M20 20.5V9" />
    </Svg>
  );
}

/** Casos: tabla con renglones — es la LISTA, no un caso suelto. */
export function IconoCasos(p: Props) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17" />
      <path d="M8.5 9.5v10" />
      <path d="M12 13h5M12 16h3" />
    </Svg>
  );
}

/** Bandeja: charola de entrada con la flecha que baja. */
export function IconoBandeja(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3.5 13.5h4l1.5 3h6l1.5-3h4" />
      <path d="M3.5 13.5L6 5.5h12l2.5 8v5a1.5 1.5 0 01-1.5 1.5H5a1.5 1.5 0 01-1.5-1.5z" />
    </Svg>
  );
}

/** Alertas: campana. */
export function IconoAlertas(p: Props) {
  return (
    <Svg {...p}>
      <path d="M6.5 10a5.5 5.5 0 1111 0c0 4 1.5 5.5 1.5 5.5H5S6.5 14 6.5 10z" />
      <path d="M10 19a2.2 2.2 0 004 0" />
    </Svg>
  );
}

/** Esperan mi respuesta: reloj de arena. */
export function IconoEsperan(p: Props) {
  return (
    <Svg {...p}>
      <path d="M7 3.5h10M7 20.5h10" />
      <path d="M8 3.5v3.2c0 1.6 4 3.7 4 5.3 0-1.6 4-3.7 4-5.3V3.5" />
      <path d="M8 20.5v-3.2c0-1.6 4-3.7 4-5.3 0 1.6 4 3.7 4 5.3v3.2" />
    </Svg>
  );
}

/** Guías: libro abierto. */
export function IconoGuias(p: Props) {
  return (
    <Svg {...p}>
      <path d="M12 6.5S10 4.5 4 4.5v13c6 0 8 2 8 2s2-2 8-2v-13c-6 0-8 2-8 2z" />
      <path d="M12 6.5v13" />
    </Svg>
  );
}

/** Enlaces: los dos eslabones. */
export function IconoEnlaces(p: Props) {
  return (
    <Svg {...p}>
      <path d="M10 13.5a4 4 0 006 .5l2.5-2.5a4 4 0 10-5.7-5.7L11.5 7" />
      <path d="M14 10.5a4 4 0 00-6-.5L5.5 12.5a4 4 0 105.7 5.7L12.5 17" />
    </Svg>
  );
}

/** Admin: personas — es gestión de usuarios. */
export function IconoAdmin(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="9.5" cy="8" r="3.5" />
      <path d="M3.5 19.5c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
      <path d="M16.5 5.2a3.5 3.5 0 010 6.6" />
      <path d="M18 14.6c1.6.8 2.5 2.4 2.5 4.9" />
    </Svg>
  );
}

/**
 * Configuración: deslizadores.
 *
 * El engrane clásico NO sirve aquí: a 18–20 px sus dientes se vuelven rayitas y
 * se lee como un solecito. Los deslizadores se distinguen a cualquier tamaño y
 * además describen mejor esta pantalla, que es pura palanca y catálogo.
 */
export function IconoConfig(p: Props) {
  return (
    <Svg {...p}>
      <path d="M4 7h6M14 7h6M4 12h10M18 12h2M4 17h3M11 17h9" />
      <circle cx="12" cy="7" r="2" />
      <circle cx="16" cy="12" r="2" />
      <circle cx="9" cy="17" r="2" />
    </Svg>
  );
}

/** Sala de cierre: bandera de meta — es donde los casos terminan. */
export function IconoSalaCierre(p: Props) {
  return (
    <Svg {...p}>
      <path d="M6 21V3.5" />
      <path d="M6 4.5h12l-2.5 4 2.5 4H6" />
    </Svg>
  );
}

/** Salir: la flecha que se va por la puerta. */
export function IconoSalir(p: Props) {
  return (
    <Svg {...p}>
      <path d="M14.5 4.5H6.5a2 2 0 00-2 2v11a2 2 0 002 2h8" />
      <path d="M17 8.5l3.5 3.5-3.5 3.5" />
      <path d="M20 12h-9.5" />
    </Svg>
  );
}

/** Más (barra del celular): los tres puntitos del menú. */
export function IconoMas(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/**
 * Modo enfoque: diana. Se conserva la metáfora del 🎯 que ya estaba, para que
 * quien lo tenía ubicado no lo pierda.
 */
export function IconoEnfoque(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Mi perfil: una persona. */
export function IconoPerfil(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8" r="3.8" />
      <path d="M4.5 20c0-3.8 3.4-6.2 7.5-6.2s7.5 2.4 7.5 6.2" />
    </Svg>
  );
}

// --- acciones del caso ------------------------------------------------------

/** Notificar a un compañero: megáfono. */
export function IconoNotificar(p: Props) {
  return (
    <Svg {...p}>
      <path d="M4 10.5v3a1.5 1.5 0 001.5 1.5H8l6 4.5V6L8 10.5H5.5A1.5 1.5 0 004 12z" />
      <path d="M17 9.5a4 4 0 010 5" />
    </Svg>
  );
}

/** Cerrar el caso: palomita en círculo — terminado, no cancelado. */
export function IconoCerrar(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 12.3l2.5 2.5 4.5-5" />
    </Svg>
  );
}

/** Duplicado: dos hojas encimadas. */
export function IconoDuplicado(p: Props) {
  return (
    <Svg {...p}>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 5.5H6.5a2 2 0 00-2 2v9" />
    </Svg>
  );
}

/** Revisar con el proveedor: traerse lo nuevo (flechas en ciclo). */
export function IconoRevisar(p: Props) {
  return (
    <Svg {...p}>
      <path d="M20 12a8 8 0 11-2.5-5.8" />
      <path d="M20 4v4.5h-4.5" />
    </Svg>
  );
}

/** Responder al proveedor: flecha de respuesta. */
export function IconoResponder(p: Props) {
  return (
    <Svg {...p}>
      <path d="M9.5 7.5L4 13l5.5 5.5" />
      <path d="M4 13h9a7 7 0 007-7V4.5" />
    </Svg>
  );
}

/** Abrir ticket con el proveedor: boleto. */
export function IconoTicket(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3.5 8.5V7a1.5 1.5 0 011.5-1.5h14A1.5 1.5 0 0120.5 7v1.5a2.5 2.5 0 000 5V17a1.5 1.5 0 01-1.5 1.5H5A1.5 1.5 0 013.5 17v-3.5a2.5 2.5 0 000-5z" />
      <path d="M13.5 6.5v2M13.5 11v2M13.5 15.5v2" />
    </Svg>
  );
}

/** Agregar: el más. */
export function IconoAgregar(p: Props) {
  return (
    <Svg {...p}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

/**
 * Ventas: etiqueta de precio con su ojal. Es el catálogo de lo VENDIDO —
 * distinto del ticket de Casos (soporte) y de las cajas de Config.
 */
export function IconoVentas(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3.5 3.5h7.3a1.5 1.5 0 011.06.44l8.2 8.2a1.5 1.5 0 010 2.12l-5.8 5.8a1.5 1.5 0 01-2.12 0l-8.2-8.2A1.5 1.5 0 013.5 10.8z" />
      <circle cx="7.8" cy="7.8" r="1.4" />
    </Svg>
  );
}

/**
 * El proveedor contestó y falta la nuestra: globo de conversación con una
 * respuesta dentro. Se distingue del sobre de Correos (eso es el cliente) y de
 * la campana de Alertas (eso es cualquier aviso).
 */
export function IconoProveedor(p: Props) {
  return (
    <Svg {...p}>
      <path d="M4 5.5h16v10H9l-5 4z" />
      <path d="M12.5 8.5L10 11l2.5 2.5" />
      <path d="M10 11h4.5a2 2 0 012 2" />
    </Svg>
  );
}

// --- buzón (feature «Revisar correo») ---------------------------------------
// Estos cuatro tapaban su hueco con emoji (📎 🔍 ✕ ✓), que es justo lo que la
// convención de la casa descartó: un emoji cambia de forma en cada sistema y no
// hereda el color del texto.

/** Adjunto: el clip de papel clásico, sin diagonal para que se lea a 18px. */
export function IconoAdjunto(p: Props) {
  return (
    <Svg {...p}>
      <path d="M20.5 11.5l-8.4 8.4a5 5 0 01-7.1-7.1l8.5-8.5a3.5 3.5 0 014.9 5l-8.5 8.5a2 2 0 01-2.8-2.8l7.8-7.8" />
    </Svg>
  );
}

/** Buscar: lupa. El mango sale a 45° para que no se confunda con el de enfoque. */
export function IconoBuscar(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8L21 21" />
    </Svg>
  );
}

/** Ignorar: la equis. NO es un bote de basura — no se borra nada, se decide. */
export function IconoIgnorar(p: Props) {
  return (
    <Svg {...p}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Svg>
  );
}

/** Seleccionar: palomita suelta, para la fila elegida. */
export function IconoSeleccionar(p: Props) {
  return (
    <Svg {...p}>
      <path d="M4.5 12.5l5 5 10-11" />
    </Svg>
  );
}

/** Ayuda de la vista: signo de interrogación en un círculo. */
export function IconoAyuda(p: Props) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.7 9.6a2.4 2.4 0 014.6.9c0 1.6-2.3 2.1-2.3 3.5" />
      <path d="M12 16.9v.1" />
    </Svg>
  );
}
